import { useEffect, useRef } from "react";

const SAVE_BAR_ID = "shopify-save-bar";

export function useShopifySaveBar({
  isDirty,
  onSave,
  onDiscard,
  canSave = true,
}: {
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  /**
   * When false, the Save button is disabled (e.g. required fields not yet
   * filled). Discard stays enabled so the merchant can always back out.
   * Defaults to true so existing callers are unaffected.
   */
  canSave?: boolean;
}) {
  const onSaveRef = useRef(onSave);
  const onDiscardRef = useRef(onDiscard);
  const elRef = useRef<any>(null);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);
  useEffect(() => {
    onDiscardRef.current = onDiscard;
  }, [onDiscard]);

  // Crear ui-save-bar con sus botones hijos una sola vez
  useEffect(() => {
    let el = document.getElementById(SAVE_BAR_ID) as any;

    if (!el) {
      el = document.createElement("ui-save-bar");
      el.id = SAVE_BAR_ID;

      // Botón Save (variant="primary")
      const saveBtn = document.createElement("button");
      saveBtn.setAttribute("variant", "primary");
      saveBtn.id = `${SAVE_BAR_ID}-save`;

      // Botón Discard (sin variant)
      const discardBtn = document.createElement("button");
      discardBtn.id = `${SAVE_BAR_ID}-discard`;

      el.appendChild(saveBtn);
      el.appendChild(discardBtn);
      document.body.appendChild(el);
    }

    elRef.current = el;

    const saveBtn = document.getElementById(`${SAVE_BAR_ID}-save`)!;
    const discardBtn = document.getElementById(`${SAVE_BAR_ID}-discard`)!;

    const handleSave = async () => {
      try {
        await onSaveRef.current();
        window.shopify?.toast?.show("Guardado correctamente");
        elRef.current?.hide?.();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Error al guardar";
        window.shopify?.toast?.show(message, { isError: true });
      }
    };

    const handleDiscard = () => {
      onDiscardRef.current();
      window.shopify?.toast?.show("Cambios descartados");
      elRef.current?.hide?.();
    };

    saveBtn.addEventListener("click", handleSave);
    discardBtn.addEventListener("click", handleDiscard);

    return () => {
      saveBtn.removeEventListener("click", handleSave);
      discardBtn.removeEventListener("click", handleDiscard);
      // Hide the bar when the host unmounts so it never lingers over a screen
      // that is no longer editing anything (e.g. after an inline form closes
      // via a path that doesn't go through Save/Discard, like a delete action).
      elRef.current?.hide?.();
    };
  }, []);

  // Mostrar/ocultar según isDirty
  useEffect(() => {
    if (!elRef.current) return;
    if (isDirty) {
      elRef.current.show?.();
    } else {
      elRef.current.hide?.();
    }
  }, [isDirty]);

  // Enable/disable the Save button based on canSave. Discard is never
  // disabled so the merchant can always abandon invalid changes.
  useEffect(() => {
    const saveBtn = document.getElementById(`${SAVE_BAR_ID}-save`);
    if (!saveBtn) return;
    if (canSave) {
      saveBtn.removeAttribute("disabled");
    } else {
      saveBtn.setAttribute("disabled", "");
    }
  }, [canSave]);
}
